import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BaseResolver } from '@app/app.resolver';
import { FeatureFlag } from '@app/enums';
import { NotFoundError } from '@app/errors/http/400/not-found.error';
import { ModelFactory } from '@app/models/core/model-factory';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { CompanyBenefit } from '@app/models/payroll/company/benefit.model';
import { CompanyDeduction } from '@app/models/payroll/company/deduction.model';
import { Payroll } from '@app/models/payroll/payroll.model';
import { RL1Summary } from '@app/models/payroll/rl1-summary.model';
import { Rl1 } from '@app/models/payroll/rl1.model';
import { Roe } from '@app/models/payroll/roe.model';
import { T4Summary } from '@app/models/payroll/t4-summary.model';
import { T4 } from '@app/models/payroll/t4.model';
import { TaxReturn } from '@app/models/payroll/tax-return.model';
import { WorkersCompensation } from '@app/models/payroll/workers-compensation.model';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class PayrollResolver extends BaseResolver {
    protected modelFactory: ModelFactory = new ModelFactory([
        {
            className: 'roe',
            production: {
                find: (id): Promise<Roe> => {
                    return Roe.param('company', this.auth.company.id).find(id);
                },
            },
        },
        {
            className: 'companyAdditionalIncome',
            production: {
                find: (id): Promise<CompanyAdditionalIncome> => {
                    return CompanyAdditionalIncome.param('company', this.auth.company.id)
                        .with(['employee_additional_incomes', 't4_boxes', 'rl1_boxes'])
                        .find(id);
                },
            },
        },
        {
            className: 'deprecated_benefit',
            production: {
                find: (id): Promise<CompanyBenefit> => {
                    return CompanyBenefit.param('company', this.auth.company.id)
                        .with(['employees', 'employee_benefits', 't4_boxes'])
                        .find(id);
                },
            },
        },
        {
            className: 'benefit',
            production: {
                find: (id): Promise<CompanyBenefit> => {
                    return CompanyBenefit.param('company', this.auth.company.id)
                        .with(['benefit_type' /*, 'benefit_type.t4_boxes'*/])
                        .find(id);
                },
            },
        },
        {
            className: 'deduction',
            production: {
                find: (id): Promise<CompanyDeduction> => {
                    return CompanyDeduction.param('company', this.auth.company.id)
                        .with(['deduction_type' /*, 'benefit_type.t4_boxes'*/])
                        .find(id);
                },
            },
        },
        {
            className: 't4',
            production: {
                find: (id): Promise<T4> => {
                    return T4.param('company', this.auth.company.id).find(id);
                },
            },
        },
        {
            className: 'rl1',
            production: {
                find: (id): Promise<Rl1> => Rl1.param('company', this.auth.company.id).find(id),
            },
        },
        {
            className: 't4Summary',
            production: {
                find: (id): Promise<T4Summary> => {
                    return T4Summary.param('company', this.auth.company.id)
                        .find(id)
                        .catch((error) => {
                            // If the summary does not exist, redirect to the root route
                            // This can happen if the summary has been deleted on the BE unknowingly
                            if (error instanceof NotFoundError) {
                                this.router.navigate(['/payroll/records/tax-returns']);
                                return new T4Summary();
                            }
                            throw error;
                        });
                },
                make: () => new T4Summary(),
            },
        },
        {
            className: 'rl1Summary',
            production: {
                find: (id): Promise<RL1Summary> => {
                    return RL1Summary.param('company', this.auth.company.id).find(id);
                },
                make: () => new RL1Summary(),
            },
        },
        {
            className: 'payroll',
            production: {
                find: (id): Promise<Payroll> => {
                    return Payroll.param('company', this.auth.company.id)
                        .with(['pay_periods', 'ept_summary', 'content_directories', 'covid_wage_subsidy'])
                        .find(id);
                },
            },
        },
        {
            className: 'workersCompensation',
            production: {
                find: (id): Promise<WorkersCompensation> => {
                    return WorkersCompensation.param('company', this.auth.company.id).find(id);
                },
            },
        },
        {
            className: 'taxReturn',
            production: {
                find: (id): Promise<TaxReturn | T4Summary> =>
                    // If the feature-flag is off we still need to return a T4Summary since the routing is the same
                    from(this.featureService.has(FeatureFlag.qcPayroll))
                        .pipe(
                            switchMap((hasQcPayrollFlag) =>
                                hasQcPayrollFlag
                                    ? TaxReturn.find(id)
                                    : this.modelFactory.generate<T4Summary>('t4Summary', id)
                            )
                        )
                        .toPromise(),
                make: (): Promise<TaxReturn | T4Summary> =>
                    from(this.featureService.has(FeatureFlag.qcPayroll))
                        .pipe(
                            switchMap((hasQcPayrollFlag) =>
                                hasQcPayrollFlag
                                    ? Promise.resolve(new TaxReturn())
                                    : this.modelFactory.generate<T4Summary>('t4Summary')
                            )
                        )
                        .toPromise(),
            },
        },
    ]);

    constructor(
        protected authService: AuthService,
        protected router: Router,
        private featureService: FeatureService
    ) {
        super(authService, router);
    }
}
